import { ResearchInPracticeSection } from '../components/sections/ResearchInPracticeSection';
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Container, Button } from '../components/ui';
import { ServiceCard } from '../components/sections';
import { WHAT_WE_DO_SERVICES, WHAT_WE_DO_MEDIA } from '../data/whatWeDo';

/*
 * Services (a.k.a. "Explore our services")
 * ----------------------------------------
 * Landing page for /services. Two zones:
 *
 *   1. A cover-video hero (the same overview webm we surface in the
 *      sidebar drawer's "What We Do" fallback preview) with a white
 *      overlay card that greets the visitor and links straight to the
 *      contact form.
 *
 *   2. A responsive grid — "Explore how we can help" — with one card
 *      per service in `WHAT_WE_DO_SERVICES`. Each card shows the
 *      service's own cover clip, the marketing description, and a
 *      Learn-more link to /services/<slug>.
 *
 * The video hero + video cards create a lot of concurrent decode
 * pressure, so the cards use hover-to-play semantics — a static first
 * frame at rest, autoplay muted-looped while the mouse is over the
 * card. Browsers handle 11 idle <video> elements comfortably; only the
 * hovered one actually plays.
 */

// Copy for the hero card. Kept in this file (not in whatWeDo.ts)
// because it describes the entire catalog, not a single service.
const OVERVIEW_DESCRIPTION =
  'Explore our full-stack services across applications, data, cloud, security, platforms, marketing, and AI automation — built to help businesses grow, adapt, and lead.';

export const Services: React.FC = () => {
  return (
    <>
      {/* ═════════════════ Breadcrumbs strip ═════════════════
          Sticky under the fixed header (see ServiceDetail.tsx for the
          full rationale — spacer + `sticky top-20 z-40` keeps the
          Home > Services trail visible throughout the page). */}
      <div style={{ height: '80px' }} aria-hidden />
      <div className="sticky top-20 z-40 bg-white border-b border-navy-100 shadow-sm">
        <Container className="py-4">
          <Breadcrumbs />
        </Container>
      </div>

      {/* ═════════════════ Hero cover ═════════════════ */}
      <ExploreHero />

      {/* ═════════════════ "Explore how we can help" grid ═════════════════
          Full-bleed: intentionally NOT wrapped in `<Container>` so the
          grid consumes the entire viewport width, mirroring the
          reference layout (Zensar / large-consultancy pattern). Only
          the horizontal padding — which grows with the breakpoint —
          keeps the cards off the exact edges. The section heading
          also sits at the LEFT edge instead of getting centered in a
          narrow container. */}
      <section className="bg-white py-16 md:py-20 lg:py-24">
        <div className="site-wide-container">
          <motion.h2
            className="heading-lg text-navy-900 mb-10 md:mb-14"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            Explore how we can help
          </motion.h2>

          {/* 1 / 2 / 3 columns depending on breakpoint. Slightly larger
              column and row gaps than a normal card grid so the
              individual services still read as distinct modules even
              though there's a lot more horizontal room to fill now. */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-8 lg:gap-x-10 gap-y-14 md:gap-y-16">
            {WHAT_WE_DO_SERVICES.map((service, i) => (
              <ServiceCard
                key={service.id}
                service={service}
                // Stagger the entrance so the whole grid doesn't pop
                // in at once — feels calmer at a glance.
                animationDelay={Math.min(i, 5) * 0.06}
              />
            ))}
          </div>
        </div>
      </section>
      {/* Show the full research catalog, as on IstaLabs. */}
      <ResearchInPracticeSection />
    </>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const Breadcrumbs: React.FC = () => (
  <nav
    aria-label="Breadcrumb"
    className="flex flex-wrap items-center gap-2 text-sm"
  >
    <Link
      to="/"
      className="text-navy-500 hover:text-accent-600 transition-colors"
    >
      Home
    </Link>
    <ChevronRight className="w-3.5 h-3.5 text-navy-400 shrink-0" aria-hidden />
    <span aria-current="page" className="font-semibold text-navy-900">
      Services
    </span>
  </nav>
);

/**
 * Hero video + overlay card. Mirrors the treatment used on
 * ServiceDetail pages, only with an "Explore our services" heading and
 * the shared What-We-Do overview webm as the source. Extracted so the
 * autoplay nudge effect lives next to the <video> it drives.
 */
const ExploreHero: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    // Some browsers (Safari especially) freeze on the first frame
    // even with autoPlay + muted; nudge them once so the video
    // actually rolls.
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, []);

  return (
    <section className="relative bg-navy-950">
      {/* Content sets the hero height; the shared minimum is capped on ultrawide screens. */}
      <div
        className="responsive-hero relative w-full overflow-hidden"
      >
        <video
          ref={videoRef}
          src={WHAT_WE_DO_MEDIA.whatWeDoOverview}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Even, slightly darker overlay so the centred card reads
            clearly against any frame of the video — the previous
            bottom-heavy gradient made sense when the card sat at the
            bottom, but with the card centred we want uniform contrast
            across the whole hero. */}
        <div
          aria-hidden
          className="absolute inset-0 bg-black/35"
        />

        {/* Card centred both horizontally AND vertically inside the
            hero, per user direction. `items-center` handles the
            vertical axis; `justify-center` the horizontal. */}
        <div className="relative flex items-center justify-center px-4 sm:px-6 py-10 sm:py-14 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className={
              // Much wider container per user request — dominates the
              // hero rather than sitting as a small poster. max-w
              // scales up through the breakpoints so the card fills a
              // sensible fraction of the viewport at every size:
              //   base   → 94% of viewport width
              //   md     → 4xl (896 px)
              //   lg     → 5xl (1024 px)
              //   xl     → 6xl (1152 px)
              'w-full max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl ' +
              'bg-white rounded-2xl border border-navy-100 ' +
              'shadow-[0_25px_70px_-15px_rgba(2,8,20,0.6)] ' +
              // Larger internal padding — bumped one step per
              // breakpoint so the card breathes at every size.
              'p-5 sm:p-8 md:p-10 lg:p-12'
            }
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[54px] font-bold text-navy-900 leading-tight mb-4 md:mb-6">
              Explore our services
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-[22px] text-navy-700 leading-relaxed mb-7 md:mb-9">
              {OVERVIEW_DESCRIPTION}
            </p>
            <Link to="/contact">
              <Button
                variant="primary"
                size="lg"
                rightIcon={<ArrowRight className="w-5 h-5" />}
              >
                Connect with our experts
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

